import React from "react";
import { Link } from "react-router-dom";
import strings from "../../blog/strings";

export function Breadcrumbs({ lang, items }) {
  const t = strings(lang);
  return (
    <nav className="blog-breadcrumbs" aria-label={t.breadcrumbAria}>
      <ol>
        {items.map((item, i) => {
          const last = i === items.length - 1;
          return (
            <li key={item.path || item.name}>
              {last || !item.path ? (
                <span aria-current="page">{item.name}</span>
              ) : (
                <Link to={item.path}>{item.name}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function PostMeta({ lang, post, showUpdated = true }) {
  const t = strings(lang);
  return (
    <p className="blog-meta">
      <span>
        {t.publishedOn}{" "}
        <time dateTime={post.datePublished}>{post.datePublishedLabel}</time>
      </span>
      {/* Data aktualizacji jest pokazywana zawsze, także gdy równa dacie publikacji —
          czytelnik ma widzieć, że tekst nie leżał nietknięty. */}
      {showUpdated && (
        <>
          <span className="blog-meta-dot" aria-hidden="true">
            ·
          </span>
          <span>
            {t.updatedOn}{" "}
            <time dateTime={post.dateModified}>{post.dateModifiedLabel}</time>
          </span>
        </>
      )}
      <span className="blog-meta-dot" aria-hidden="true">
        ·
      </span>
      <span>{t.readingTime(post.readingMinutes)}</span>
    </p>
  );
}

export function PostCard({ lang, post, headingLevel = 2 }) {
  const Heading = `h${headingLevel}`;
  return (
    <article className="blog-card">
      <Heading className="blog-card-title">
        <Link to={post.path}>{post.title}</Link>
      </Heading>
      <PostMeta lang={lang} post={post} showUpdated={false} />
      <p className="blog-card-desc">{post.description}</p>
    </article>
  );
}

export function TagList({ lang, tags, allTags }) {
  const t = strings(lang);
  if (!tags.length) return null;
  return (
    <div className="blog-tags">
      <span className="blog-tags-label">{t.tags}:</span>
      <ul>
        {tags.map((tag) => {
          const entry = allTags.find((x) => x.tag === tag);
          return (
            <li key={tag}>
              {entry ? <Link to={entry.path}>#{tag}</Link> : <span>#{tag}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Pagination({ lang, page, pages }) {
  const t = strings(lang);
  if (pages.length <= 1) return null;
  const prev = pages.find((p) => p.number === page.number - 1);
  const next = pages.find((p) => p.number === page.number + 1);
  return (
    <nav className="blog-pagination" aria-label={t.pageTitle(page.number)}>
      {prev ? (
        <Link className="blog-pagination-prev" to={prev.path} rel="prev">
          {t.prevPage}
        </Link>
      ) : (
        <span />
      )}
      <ol className="blog-pagination-list">
        {pages.map((p) => (
          <li key={p.number}>
            {p.number === page.number ? (
              <span aria-current="page">{p.number}</span>
            ) : (
              <Link to={p.path}>{p.number}</Link>
            )}
          </li>
        ))}
      </ol>
      {next ? (
        <Link className="blog-pagination-next" to={next.path} rel="next">
          {t.nextPage}
        </Link>
      ) : (
        <span />
      )}
    </nav>
  );
}

export function AuthorBox({ lang, author }) {
  const t = strings(lang);
  return (
    <aside className="blog-author-box" aria-labelledby="blog-author-heading">
      <h2 id="blog-author-heading">{t.aboutAuthor}</h2>
      <p className="blog-author-name">
        <Link to={author.path}>{author.name}</Link>
        {author.role && <span className="blog-author-role"> — {author.role}</span>}
      </p>
      {author.credentials && author.credentials.length > 0 && (
        <ul className="blog-author-credentials">
          {author.credentials.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      )}
    </aside>
  );
}

export function RelatedPosts({ lang, posts }) {
  const t = strings(lang);
  if (!posts.length) return null;
  return (
    <section className="blog-related" aria-labelledby="blog-related-heading">
      <h2 id="blog-related-heading">{t.relatedTitle}</h2>
      <ul>
        {posts.map((p) => (
          <li key={p.slug}>
            <Link to={p.path}>{p.title}</Link>
            <span className="blog-related-desc">{p.description}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
